import type { CrowdexAssistant } from '@crowdex/agent-contracts/api';
import type { ReactNode } from 'react';
import { delegatesSummary, placeLine } from './assistant-model';
import { PROJECT_SETTINGS_VIEW, useOpenAssistantPanel } from './assistant-panel-tabs';
import { PersonaAvatar } from './assistant-parts';
import {
  useAssistantAutomations,
  useAssistantOfConversation,
  useAssistantPersonas,
} from './use-assistant';

/**
 * What the header says of an assistant's channel (D65): its saved agents' marks, "3 agents · 4
 * automations" (opening its settings tab) and "Since Oct 2".
 */
export function AssistantHeading({
  project,
  conversationId,
}: {
  project: CrowdexAssistant;
  /** The shown conversation, whose panel opens. */
  conversationId: string;
}) {
  const automations = useAssistantAutomations(project);
  const openPanel = useOpenAssistantPanel();
  const personas = useAssistantPersonas(project);
  return (
    // Its parts sit in the header's row: the place line gives way first, then a narrow header
    // drops the avatars, then the project's name truncates; the counts stay whole (L7).
    <div className="contents" data-project-heading>
      {personas.length > 0 && (
        <span className="flex shrink-0 -space-x-1.5 @max-lg:hidden">
          {personas.slice(0, 4).map((persona) => (
            <PersonaAvatar
              className="ring-2 ring-desktop-chrome"
              key={persona.agentId}
              persona={persona}
              personas={personas}
              size="sm"
            />
          ))}
        </span>
      )}
      <button
        className="pointer-events-auto shrink-0 whitespace-nowrap text-muted-foreground text-xs hover:text-foreground"
        onClick={() => openPanel(conversationId, { view: PROJECT_SETTINGS_VIEW })}
        type="button"
      >
        {delegatesSummary(personas.length, automations?.automations.length ?? null)}
      </button>
      <span className="pointer-events-none min-w-0 shrink-[100] truncate text-muted-foreground text-xs">
        {placeLine(project)}
      </span>
    </div>
  );
}

/**
 * A phone's header for a project's conversation (mobile 2d): its colour and name, then "3 agents ·
 * 4 automations". Null for any other conversation.
 */
export function useAssistantMobileTitle(
  conversationId: string | null,
): { title: ReactNode; subtitle: string } | null {
  const project = useAssistantOfConversation(conversationId);
  const automations = useAssistantAutomations(project);
  const personas = useAssistantPersonas(project);
  if (project === null) return null;
  return {
    title: (
      <span className="inline-flex max-w-full items-center gap-2">
        <span
          aria-hidden
          className="size-3 shrink-0 rounded-[3px]"
          style={{ backgroundColor: project.color }}
        />
        <span className="truncate">{project.name}</span>
      </span>
    ),
    subtitle: delegatesSummary(personas.length, automations?.automations.length ?? null),
  };
}
